/**
 * 視聴 UI 上にユーザー識別子を半透明オーバーレイ表示するウォーターマーク。
 *
 * 目的（F-PREV-12）:
 *   - スクリーンキャプチャを完全に防止することは不可能だが、
 *     撮影した本人を後追いできるよう識別子を画面に焼き付ける
 *   - メアド or ユーザーID短縮 + 表示時刻（hh:mm）を入れて、
 *     どのセッションの撮影かまで遡れるようにする
 *
 * 実装上のポイント:
 *   - `pointer-events: none` でクリック透過（裏のプレイヤー操作を阻害しない）
 *   - 斜めに何度も反復（領域全面を覆う）
 *   - 透過度は 0.08〜0.10（控えめだが消すと違和感）
 *   - mix-blend-mode で背景色に応じて読める色になる
 */
type Props = {
  /** 表示する識別子（メアド or 共有URL token + 視聴者ID 等） */
  identifier: string
  /** 追加の補足（任意。例: 視聴開始時刻） */
  hint?: string
  /** デフォルトの斜め配置を変えたい場合のみ override */
  className?: string
}

export function Watermark({ identifier, hint, className }: Props) {
  // 表示文言。改竄追跡時は時刻が決め手になるので刻む。
  const stamp = formatTimeStamp(new Date())
  const label = hint ? `${identifier} · ${hint} · ${stamp}` : `${identifier} · ${stamp}`

  // 同じテキストを 8 列 × 6 行で繰り返す。
  const rows = 6
  const cols = 8

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden select-none ${className ?? ''}`}
      // mix-blend は子で適用する
    >
      <div
        className="absolute inset-0 flex flex-col justify-around"
        style={{
          transform: 'rotate(-22deg) scale(1.4)',
          transformOrigin: 'center',
        }}
      >
        {Array.from({ length: rows * cols }).map((_, idx) => {
          const ri = Math.floor(idx / cols)
          const ci = idx % cols
          // 静的レイアウトなので順序が変わらない。row+col の合成で衝突しない安定キー。
          const stableKey = `wm-${ri}-${ci}`
          if (ci === 0) {
            return (
              <div key={`row-${stableKey}`} className="flex justify-around whitespace-nowrap">
                {Array.from({ length: cols }).map((__, ci2) => (
                  <span
                    key={`cell-${ri}-${ci2}-${label}`}
                    className="text-[11px] md:text-xs font-mono tracking-wider text-white/15 mix-blend-overlay"
                    style={{ marginLeft: `${(ri % 2) * 6}%` }}
                  >
                    {label}
                  </span>
                ))}
              </div>
            )
          }
          return null
        })}
      </div>
    </div>
  )
}

function formatTimeStamp(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
}
